/**
 * MemberPicker — lightweight member typeahead used to attach an assessment to
 * a member.
 *
 * Mirrors the app's existing member-search pattern (the referrals / enrollment
 * flows call GET /api/enrollments/search-members). This variant goes through
 * authFetch so the X-CBO-ID header is always sent.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { authFetchJson } from '@/lib/authFetch';

export interface PickedMember {
  id: number;
  name: string;
  // members.email from the search. Optional so callers that build a
  // PickedMember by hand (a fixed member id) don't have to supply it.
  email?: string | null;
}

interface MemberSearchResponse {
  members?: Array<{
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }>;
}

interface MemberPickerProps {
  value: PickedMember | null;
  onChange: (member: PickedMember | null) => void;
  label?: string;
  placeholder?: string;
  id?: string;
  /** Show each result's email under the name, to tell same-named members apart. */
  showEmail?: boolean;
}

export const MemberPicker: React.FC<MemberPickerProps> = ({
  value,
  onChange,
  label = 'Member',
  placeholder = 'Search by name…',
  id = 'member-picker',
  showEmail = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const data = await authFetchJson<MemberSearchResponse>(
        `/api/enrollments/search-members?search=${encodeURIComponent(q)}&limit=10`,
      );
      setResults(
        (data.members || []).map((m) => ({
          id: m.id,
          name: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || `Member ${m.id}`,
          email: m.email ?? null,
        })),
      );
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounce the search as the user types.
  useEffect(() => {
    if (value) return; // don't search while a member is selected
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void runSearch(query);
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, runSearch, value]);

  if (value) {
    return (
      <div>
        <label htmlFor={id} className="mb-1 block text-sm font-medium">
          {label}
        </label>
        <div
          id={id}
          className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
        >
          <span className="font-medium">{value.name}</span>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setResults([]);
            }}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            aria-label="Clear selected member"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id={id}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
        />
        {searching && (
          <Loader2
            className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white py-1 shadow-md dark:bg-gray-900"
        >
          {results.map((m) => (
            <li key={m.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => {
                  onChange(m);
                  setOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
              >
                {m.name}
                {showEmail && m.email && (
                  <span className="block text-xs text-muted-foreground">{m.email}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !searching && query.trim() && results.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm text-muted-foreground shadow-md dark:bg-gray-900">
          No members found.
        </div>
      )}
    </div>
  );
};
